import { zodResolver } from "@hookform/resolvers/zod";
import { Activity, type ChangeEvent, useEffect, useRef } from "react";
import { type SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { z } from "zod";

import { SubmitButton } from "@/components/buttons/submit-button";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CircleCheckIcon, CircleXIcon } from "@/icons";
import type { SERVICES } from "@/lib/constants";
import { sanitizeAlphanumeric } from "@/lib/utils";
import {
  useGetServiceProvidersQuery,
  useLazyCheckBlacklistQuery,
} from "@/redux/services/order-portal";
import { setOrder } from "@/redux/slices/order";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { checkBlacklistSchema } from "@/shared/schemas/check-blacklist";

export const CheckBlacklistForm = ({
  service,
  isOpen,
  onOpenChange,
  className,
}: {
  service: Exclude<
    (typeof SERVICES)[number],
    { label: "EASI Tourist" } | { label: "EASI Recaj" }
  >;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const lastCheckedNricRef = useRef<string>("");

  const order = useAppSelector((state) => state.order);
  const dispatch = useAppDispatch();
  const { data: serviceProviders } = useGetServiceProvidersQuery({
    plan_type: service.planType,
  });
  const [
    checkBlacklist,
    { currentData: isBlacklisted, isLoading, isSuccess, reset },
  ] = useLazyCheckBlacklistQuery();

  const form = useForm<z.infer<typeof checkBlacklistSchema>>({
    resolver: zodResolver(checkBlacklistSchema),
    defaultValues: {
      nricNumber: "",
      hasExistingServiceProvider: false,
    },
  });

  const nricNumber = useWatch({
    control: form.control,
    name: "nricNumber",
  });
  const hasExistingServiceProvider = useWatch({
    control: form.control,
    name: "hasExistingServiceProvider",
  });
  const existingServiceProvider = useWatch({
    control: form.control,
    name: "existingServiceProvider",
  });

  async function handleCheckBlacklist() {
    try {
      const sanitizedNric = sanitizeAlphanumeric(form.getValues("nricNumber"));
      form.setValue("nricNumber", sanitizedNric, { shouldValidate: true });

      const response = await checkBlacklist({
        customer_type: service.customerType,
        id_number: sanitizedNric,
      });
      if (response.error) {
        throw new Error("Failed to check blacklist");
      }

      if (response.data === false) {
        lastCheckedNricRef.current = sanitizedNric;
      } else {
        lastCheckedNricRef.current = "";
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  }

  function getSelectedExistingServiceProvider() {
    return serviceProviders?.find(
      (provider) => provider.name === existingServiceProvider
    );
  }

  const onSubmit: SubmitHandler<z.infer<typeof checkBlacklistSchema>> = async (
    data
  ) => {
    const currentNric = sanitizeAlphanumeric(form.getValues("nricNumber"));
    form.setValue("nricNumber", currentNric, { shouldValidate: true });

    const hasCheckedCurrent =
      isBlacklisted !== undefined && lastCheckedNricRef.current === currentNric;

    if (!hasCheckedCurrent) {
      await handleCheckBlacklist();
      return;
    }

    if (isBlacklisted) {
      return;
    }

    const updatedOrder = {
      ...order,
      orderType: data.hasExistingServiceProvider ? "Port-IN" : "New Order",
      hasExistingServiceProvider: data.hasExistingServiceProvider,
      nricNumber: currentNric,
      existingServiceProvider: getSelectedExistingServiceProvider(),
      msisdnNumber: data.existingServiceNumber,
      ...(service.planType === "Infinity"
        ? { homeNumber: data.existingServiceNumber }
        : { mobileNumber: data.existingServiceNumber }),
      internalPortin: !!(
        data.existingServiceProvider === "Infinity" ||
        data.existingServiceProvider === "EASI"
      ),
      customerType: service.customerType,
    };

    dispatch(setOrder(updatedOrder));
    onOpenChange(false);
    form.reset();
    reset();

    navigate(`${service.href}/plans`);
  };

  useEffect(() => {
    if (!isOpen) {
      form.reset();
      lastCheckedNricRef.current = "";
      reset();
    }
  }, [isOpen]);

  useEffect(() => {
    lastCheckedNricRef.current = "";
    reset();
  }, [nricNumber]);

  return (
    <Form {...form}>
      <form className={className} onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="nricNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("check-blacklist.title")}</FormLabel>
              <FormControl>
                <InputGroup className="w-4/5">
                  <InputGroupInput
                    placeholder={`${t("labels.nric-number")}*`}
                    {...field}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const sanitizedValue = sanitizeAlphanumeric(
                        event.target.value
                      );

                      field.onChange(sanitizedValue);
                    }}
                  />

                  <Activity mode={isSuccess ? "visible" : "hidden"}>
                    <InputGroupAddon align="inline-end">
                      {!isBlacklisted && (
                        <CircleCheckIcon className="size-6 fill-secondary" />
                      )}
                      {isBlacklisted && (
                        <CircleXIcon className="size-6 fill-destructive" />
                      )}
                    </InputGroupAddon>

                    <Button
                      className="absolute inset-y-0 -right-16 flex items-center font-medium text-destructive peer-disabled:opacity-50"
                      onClick={() => {
                        field.onChange("");
                        reset();
                      }}
                      type="button"
                      variant="link"
                    >
                      {t("labels.clear")}
                    </Button>
                  </Activity>
                </InputGroup>
              </FormControl>
              <FormMessage />
              <Activity mode={isBlacklisted ? "visible" : "hidden"}>
                <p className="font-medium text-destructive text-sm">
                  {t("errors.blacklisted")}
                </p>
              </Activity>
            </FormItem>
          )}
        />

        {isBlacklisted !== undefined && !isBlacklisted && (
          <>
            <FormField
              control={form.control}
              name="hasExistingServiceProvider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("labels.hasExistingServiceProvider")}
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      className="flex"
                      onValueChange={(value) => field.onChange(value === "yes")}
                      value={field.value ? "yes" : "no"}
                    >
                      {[
                        { label: t("labels.yes"), value: "yes" },
                        { label: t("labels.no"), value: "no" },
                      ].map(({ label, value }) => (
                        <Label
                          className="flex items-start rounded-full border p-2 hover:bg-accent/50 has-data-[state=checked]:border-primary"
                          key={value}
                        >
                          <RadioGroupItem
                            className="shadow-none data-[state=checked]:border-primary *:data-[slot=radio-group-indicator]:[&>svg]:fill-primary *:data-[slot=radio-group-indicator]:[&>svg]:stroke-white"
                            id={value}
                            value={value}
                          />
                          <div className="font-medium">{label}</div>
                        </Label>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {hasExistingServiceProvider && (
              <>
                <FormField
                  control={form.control}
                  name="existingServiceProvider"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="sr-only">
                        {t("labels.existingServiceProvider")}
                      </FormLabel>
                      <Select
                        defaultValue={field.value}
                        onValueChange={field.onChange}
                        {...field}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={`${t(
                                "placeholders.existingServiceProvider"
                              )}*`}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {serviceProviders?.map((provider) => (
                            <SelectItem key={provider.id} value={provider.name}>
                              {provider.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="existingServiceNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="sr-only">
                        {t("labels.existingServiceNumber")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={`${
                            service.planType === "Infinity"
                              ? t("labels.land-line-number")
                              : t("labels.mobile-number")
                          }*`}
                          type="tel"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
          </>
        )}

        <div className="flex items-center gap-6">
          <Button
            className="w-full rounded-full"
            onClick={() => {
              onOpenChange(false);
              form.reset();
              reset();
            }}
            type="button"
            variant="outline"
          >
            {t("labels.back")}
          </Button>
          {isBlacklisted !== undefined && !isBlacklisted ? (
            <SubmitButton
              className="w-full"
              isSubmitting={form.formState.isSubmitting}
            >
              {t("labels.proceed")}
            </SubmitButton>
          ) : (
            <SubmitButton
              className="w-full"
              isSubmitting={isLoading}
              onClick={handleCheckBlacklist}
              type="button"
            >
              {t("labels.proceed")}
            </SubmitButton>
          )}
        </div>
      </form>
    </Form>
  );
};
